import { type FC, useState } from 'react'
import classNames from 'classnames'
import styles from './InvitationEmailPreview.module.scss'
import { Button } from '../../../components/Button/Button'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { Spinner } from '../../../components/Spinner/Spinner'
import { type BookingClient, EmailTemplatePurpose, type Location, type MeetingMode, type SchedulingEventType } from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { type LocationInput } from '../../../store/scheduling/dto'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { useSchedulingEmailTemplates } from '../shared/useSchedulingEmailTemplates'
import { clientDisplayName, clientKey } from '../shared/schedulingDisplay'
import { unsubscribeLink } from './clientInviteLinks'
import { buildEmailTemplateContext } from './buildEmailTemplateContext'
import { renderEmailHtmlBody, renderEmailSubject } from './renderEmailTemplate'
import { sanitizeEmailHtml } from './emailHtml'

interface InvitationEmailPreviewProps {
  purpose: EmailTemplatePurpose
  eventType: SchedulingEventType
  clients: BookingClient[]
  owner?: User
  ownerName: string
  meetingMode?: MeetingMode
  location?: Location | LocationInput | null
  bookingLink?: string
  originalStart?: string
  onCopyEmail: (client?: BookingClient) => void
  copyDisabled?: boolean
  copyDisabledReason?: string
}

interface PreviewCopy {
  title: string
  description: string
}

const PREVIEW_COPY: Partial<Record<EmailTemplatePurpose, { one: PreviewCopy; many: PreviewCopy }>> = {
  [EmailTemplatePurpose.INVITATION_TO_BOOK]: {
    one: { title: 'Invitation email', description: 'This email gets sent to the client with the booking page link.' },
    many: { title: 'Invitation emails', description: 'These emails get sent to clients with the booking page link.' },
  },
  [EmailTemplatePurpose.INVITATION_TO_REBOOK]: {
    one: { title: 'Reschedule email', description: 'This email asks the client to pick a new time.' },
    many: { title: 'Reschedule emails', description: 'These emails ask the clients to pick a new time.' },
  },
}

// Renders into the parent's two-column grid (label cell + value cell)
export const EmailField: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <div className='paragraph-small zocks-text-gray-500'>{label}</div>
    <div className='paragraph-small'>{children}</div>
  </>
)

interface EmailPaginationProps {
  clients: BookingClient[]
  activeIndex: number
  onActiveIndexChange: (index: number) => void
}

const EmailPagination: FC<EmailPaginationProps> = ({ clients, activeIndex, onActiveIndexChange }) => (
  <div className='d-flex align-items-center justify-content-center zocks-gap-0.5'>
    <Button
      variant='borderless'
      type='button'
      className='rounded-circle p-1'
      aria-label='Previous email'
      disabled={activeIndex === 0}
      onClick={() => onActiveIndexChange(activeIndex - 1)}
    >
      <i className='bi bi-chevron-left' aria-hidden='true' />
    </Button>
    <div className='d-flex align-items-center zocks-gap-0.5'>
      {clients.map((client, index) => (
        <span
          key={clientKey(client)}
          className={classNames(styles.dot, 'rounded-circle', index === activeIndex ? 'zocks-bg-color-primary' : 'zocks-bg-color-gray-200')}
        />
      ))}
    </div>
    <Button
      variant='borderless'
      type='button'
      className='rounded-circle p-1'
      aria-label='Next email'
      disabled={activeIndex === clients.length - 1}
      onClick={() => onActiveIndexChange(activeIndex + 1)}
    >
      <i className='bi bi-chevron-right' aria-hidden='true' />
    </Button>
  </div>
)

// Right column for the "Let the client choose" mode: the email the client receives, rendered from
// the account's email template for the purpose — the same template Push to draft uses.
export const InvitationEmailPreview: FC<InvitationEmailPreviewProps> = ({
  purpose,
  eventType,
  clients,
  owner,
  ownerName,
  meetingMode,
  location,
  bookingLink,
  originalStart,
  onCopyEmail,
  copyDisabled = false,
  copyDisabledReason,
}) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: accountResponse } = useGetAccountQuery({ accountId })
  const { byPurpose, isLoading, isError } = useSchedulingEmailTemplates()
  const [activeIndex, setActiveIndex] = useState(0)

  const pageIndex = Math.min(activeIndex, Math.max(clients.length - 1, 0))
  const client = clients[pageIndex]
  const template = byPurpose(purpose)
  const subject = template?.subject || ''
  const body = template?.body || ''
  const hasTemplate = subject !== '' && body !== ''
  const isCopyDisabled = copyDisabled || !hasTemplate
  const copyReason = !hasTemplate ? 'Set up an email template in Setup first.' : copyDisabledReason
  const purposeCopy = PREVIEW_COPY[purpose]
  const { title, description } = (clients.length > 1 ? purposeCopy?.many : purposeCopy?.one) ?? { title: 'Email preview', description: '' }
  const context = buildEmailTemplateContext({
    eventType,
    client,
    owner,
    meetingMode,
    location,
    bookingLink: bookingLink || client?.inviteUrl || '[booking link]',
    unsubscribeLink: unsubscribeLink(client, purpose) ?? '[unsubscribe link]',
    account: accountResponse?.data,
    originalStart,
  })

  return (
    <div className='d-flex flex-column zocks-gap-0.5'>
      <h5 className='mb-0'>{title}</h5>
      <div className='paragraph-small zocks-text-gray-600'>{description}</div>
      <div className='d-flex flex-column mt-2 border zocks-border-color-gray-200 zocks-border-1.0 overflow-hidden zocks-bg-color-white'>
        <div className={`p-3 zocks-bg-color-gray-100 ${styles.fields}`}>
          <EmailField label='From'>
            {ownerName} via Zocks <span className='zocks-text-gray-500'>· bookings@zocks.io</span>
          </EmailField>
          <EmailField label='To'>
            {client ? (
              <>
                {clientDisplayName(client)} <span className='zocks-text-gray-500'>· {client.email}</span>
              </>
            ) : (
              <span className='zocks-text-gray-500'>Your client</span>
            )}
          </EmailField>
          <EmailField label='Subject'>{hasTemplate ? renderEmailSubject(subject, context) : '—'}</EmailField>
        </div>
        <div className='d-flex flex-column gap-3 p-3'>
          {isLoading ? (
            <div className='d-flex justify-content-center py-4'>
              <Spinner />
            </div>
          ) : isError ? (
            <div className='paragraph-medium zocks-text-gray-600'>Your email templates could not be loaded. Please try again.</div>
          ) : hasTemplate ? (
            <div
              className={`paragraph-medium ${styles.body}`}
              dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(renderEmailHtmlBody(body, context), { neutralizeLinks: true }) }}
            />
          ) : (
            <div className='paragraph-medium zocks-text-gray-600'>No email template is set up for this email yet — configure one in Setup.</div>
          )}
          <div className='d-flex zocks-gap-0.5'>
            <Tooltip title={isCopyDisabled ? copyReason : undefined}>
              <Button variant='secondary' type='button' disabled={isCopyDisabled} title={copyReason} onClick={() => onCopyEmail(client)}>
                <i className='bi bi-copy me-1' />
                Copy email
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
      {clients.length > 1 ? (
        <div className='mt-3'>
          <EmailPagination clients={clients} activeIndex={pageIndex} onActiveIndexChange={setActiveIndex} />
        </div>
      ) : null}
    </div>
  )
}
